<template>
  <div v-if="ready">
    <TwHeader>
      <template #left>
        <tw-breadcrumbs :pages="breadcrumbs" class="mb-2" />
      </template>
    </TwHeader>
    <div class="container mt-4">
      <div class="flex-container">
        <div class="flex-item pa-2" :style="{ width: `${tableWidthLeft}px` }">
          <SelectableTradingList
            :newFlag="newFlag"
            :canAddNewTrading="((isShipper || isFwd) && isPic) && !forbidden"
            :selectedTradingId="selectedTradingId"
            :selectedItem="targetTrading"
            @select="selectItem"
            @handleCreate="handleCreate"
            @flowUpdateDatetimeChange="handleFlowUpdateDatetimeChange"
          />
        </div>
        <div class="flex-item table">
          <TradingFlowList
            :newFlag="newFlag"
            :canAddNewFlow="canAddNewFlow"
            :isOwnerFlg="isOwnerFlg"
            :selectedTradingId="selectedTradingId"
            :targetTrading="targetTrading"
            :onToggleCollapse="onToggleCollapse"
            :flowUpdateDatetime="flowUpdateDatetime"
            @cancel="handleCancel"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  watch,
  computed,
  onMounted,
} from '@vue/composition-api';

import { ABLE_FLG } from 'lib-tw-common';

import { useBffTradingManagesEditor } from '@/composables/useBffTradingManagesEditor';

// components
import TwButton from '@/components/atoms/TwButton';
import TwDropDownTsv from '@/components/atoms/TwDropDownTsv';
import TwBreadcrumbs from '@/components/molecules/TwBreadcrumbs';

// vue3 components
import TwHeader from '@/components/vue3/TwHeader.vue';

import SelectableTradingList from './components/SelectableTradingList.vue';
import TradingFlowList from './components/TradingFlowList.vue';

export default defineComponent({
  name: 'TradingList',
  components: {
    TwButton,
    TwDropDownTsv,
    TwHeader,
    TwBreadcrumbs,
    TradingFlowList,
    SelectableTradingList,
  },
  props: {
    onToggleCollapse: {
      type: Function,
      default: () => {},
    },
  },
  setup(_, { root }) {
    const ready = ref(false);
    const tableRef = ref(null);
    const tableWidthLeft = ref(320);
    const selectedTradingId = ref('');
    const targetTrading = ref(null);
    const newFlag = ref(false);
    const canAddNewFlow = ref(false);
    const isOwnerFlg = ref(false);
    const flowUpdateDatetime = ref([]);

    const fid = computed(() => root.$route.query.fid);
    const companyId = computed(() => {
      return root.$store.state.companyId;
    });

    const breadcrumbs = computed(() => [
      { label: root.$t('BreadCrumbs.Dashboard'), to: '/' },
      { label: root.$t('BreadCrumbs.Trading List') },
    ]);

    const isShipper = computed(() => {
      // カンパニーロールが荷主か
      return root.$store.getters.isShipper;
    });

    const isPic = computed(() => {
      return root.$store.state.userInformationV2?.picFlg;
    })

    const isFwd = computed(() => {
      return root.$store.getters.isFwd;
    });

    const managesEditorParams = {
      immediate: false,
    };

    const {
      forbidden,
      tradingInfo: editorTradingInfo,
      fetchData: fetchEditData,
    } = useBffTradingManagesEditor(managesEditorParams);

    watch(fid, newFid => {
      const fid = newFid || '';
      selectedTradingId.value = fid;
    });

    watch(
      () => selectedTradingId.value,
      newVal => {
        if (!newVal) return;
        if (isShipper.value || isPic.value) {
          fetchEditData({
            path: {
              companyId: companyId.value,
            },
            queries: {
              tradingId: newVal,
            },
          });
        }
      },
    );

    watch(
      () => editorTradingInfo.value,
      newVal => {
        canAddNewFlow.value = newVal.canAddNewFlowFlg === ABLE_FLG.TRUE;
        isOwnerFlg.value = newVal.isOwnerFlg === 1; 
      },
    );

    const selectItem = item => {
      newFlag.value = false;
      targetTrading.value = null;
      if (item) {
        targetTrading.value = item;
        const currentFid = root.$route.query.fid;
        if (currentFid === item.tradingId) {
          return;
        }
        root.$router.replace({
          query: {
            ...root.$route.query,
            fid: item.tradingId,
          },
        });
      } else if (root.$route.query.fid) {
        // 選択クリア時(フィルター結果0件など)は URL の fid を削除し未選択状態に同期する
        const newQuery = { ...root.$route.query };
        delete newQuery.fid;
        root.$router.replace({ query: newQuery });
      }
    };

    const getColumnWidths = () => {
      const tableEl = tableRef.value?.$el;
      if (!tableEl) return;
      const ths = tableEl.querySelectorAll('thead th');
      const widths = Array.from(ths).map(th => th.offsetWidth);
    };

    const handleCreate = () => {
      newFlag.value = !newFlag.value;
    };

    const handleCancel = () => {
      newFlag.value = false;
    };

    const handleFlowUpdateDatetimeChange = val => {
      flowUpdateDatetime.value = val;
    };

    onMounted(() => {
      if (fid.value) {
        selectedTradingId.value = fid.value;
      }
      ready.value = true;
    });

    return {
      isShipper,
      isFwd,
      isPic,
      forbidden,
      ready,
      selectedTradingId,
      breadcrumbs,
      tableRef,
      tableWidthLeft,
      targetTrading,
      selectItem,
      getColumnWidths,
      handleCreate,
      handleCancel,
      newFlag,
      canAddNewFlow,
      isOwnerFlg,
      flowUpdateDatetime,
      handleFlowUpdateDatetimeChange,
    };
  },
});
</script>

<style>
/* ---

 打消し用

--- */
.route-TradingListV3 .el-main {
  background-color: #fff;
  padding: 0px !important;
}
</style>

<style scoped>
/* ---

 TwHeader

--- */
.container {
  padding: 5px 15px 15px;
}

.flex-container {
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: space-between;
  gap: 8px;
}
.flex-row {
  flex-grow: 1;
}
.flex-item {
  width: 100%;
}

.table {
  width: calc(100% - 340px);
}

.title {
  font-size: 16px;
  font-weight: 600;
}

/* スクロールバーを非表示（デフォルト） */
.el-table__body-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
  opacity: 0;
}
/* ホバー時に表示 */
.el-table__body-wrapper:hover::-webkit-scrollbar {
  opacity: 1;
}
/* スクロールバーのスタイル */
.el-table__body-wrapper::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}
.el-table__body-wrapper::-webkit-scrollbar-track {
  background-color: transparent;
}
</style>
